import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Link, Typography } from '@mui/material';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canViewProductLinks } from '../../../config/permissions';
import { openDailySourceBatch, readDailySourceLinks } from '../dailySourcing';
import { readNokeError } from './format';
import { buildSourcingRequest, missingLinkCount, primaryLinks, SourcingLine, SourcingRequest } from './sourcing';
import { C, MONO } from './tokens';

// 「where is the 下單連結 for me and Adam?」 (Magnus 2026-09-15). Two named accounts see supplier
// links; 門市人員 never do — the backend is the real gate (canViewSourcing → 403 此功能僅限 Magnus
// 與 Adam), this is only so the tab does not offer what the API would refuse.
export interface Sourcing {
  identityKey: string;
  allowed: boolean;
  // No 'loading': nothing on the card changes until the links are in hand, so a third state would
  // only add a render (and an act() warning) for a view nobody sees.
  status: 'hidden' | 'ready' | 'error';
  links: Record<string, string[]>;
  error: string;
  retry: () => void;
}

type SourcingState = Omit<Sourcing, 'retry'>;
const empty = (identityKey: string): SourcingState => ({
  identityKey, allowed: false, status: 'hidden', links: {}, error: '',
});

/** One capability check and one stored-link lookup, shared by 訂貨 and 選品. */
export function useSourcing(lines: SourcingLine[], enabled = true): Sourcing {
  const { currentUser } = useAuth();
  const eligible = enabled && canViewProductLinks(currentUser?.id) && currentUser?.role !== 'counter';
  const requestKey = JSON.stringify(buildSourcingRequest(lines));
  const identityKey = `${currentUser?.id ?? ''}:${currentUser?.role ?? ''}:${requestKey}`;
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<SourcingState>(() => empty(identityKey));

  const retry = useCallback(() => { setState(empty(identityKey)); setRevision((value) => value + 1); }, [identityKey]);

  useEffect(() => {
    let active = true;
    setState(empty(identityKey));
    if (!eligible) return () => { active = false; };
    const request: SourcingRequest = JSON.parse(requestKey);
    (async () => {
      try {
        const capability = await api.get('/api/noke/merchandising/capabilities');
        if (!active || capability.data?.sourcing_links !== true) return;
        if (request.error) {
          setState({ ...empty(identityKey), allowed: true, status: 'error', error: request.error });
          return;
        }
        if (!request.racks.length) { setState({ ...empty(identityKey), allowed: true, status: 'ready' }); return; }
        const { data } = await api.post('/api/noke/merchandising/sourcing-links', { racks: request.racks });
        if (!active) return;
        if (data?.allowed !== true) { setState(empty(identityKey)); return; }
        setState({
          identityKey, allowed: true, status: 'ready', error: '',
          links: readDailySourceLinks(data, request.racks),
        });
      } catch (err) {
        if (!active) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        // 401/403 is the answer, not a failure to report: this account simply does not see links.
        if (status === 401 || status === 403) { setState(empty(identityKey)); return; }
        setState({ ...empty(identityKey), status: 'error', error: readNokeError(err, '無法取得 Sinsang 連結，請重試。') });
      }
    })();
    return () => { active = false; };
  }, [eligible, identityKey, requestKey, revision]);

  // Coming back from another tab rechecks permission before showing cached supplier URLs again.
  useEffect(() => {
    if (!eligible) return undefined;
    window.addEventListener('focus', retry);
    return () => window.removeEventListener('focus', retry);
  }, [eligible, retry]);

  return { ...(eligible && state.identityKey === identityKey ? state : empty(identityKey)), retry };
}

/** The one link on an order line. Nothing at all when no URL is stored for that SKU. */
export const SinsangLink: React.FC<{ sourcing: Sourcing; sku: string }> = ({ sourcing, sku }) => {
  if (!sourcing.allowed || sourcing.status !== 'ready') return null;
  const links = sourcing.links[sku] || [];
  if (!links.length) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: '4px' }}>
      {links.slice(0, 2).map((url, index) => (
        <Link
          key={url} href={url} target="_blank" rel="noopener noreferrer"
          aria-label={`${sku} ${index ? `Sinsang 備選連結 ${index}` : '下單連結'}`}
          sx={{ fontSize: 11.5, fontWeight: 800, color: C.fetch, textDecorationColor: C.fetch }}
        >
          {index ? `備選 ${index}` : '下單連結 ↗'}
        </Link>
      ))}
    </Box>
  );
};

/** The single 「連結拿不到」 message for the whole screen, with its own retry. */
export const SinsangNotice: React.FC<{ sourcing: Sourcing }> = ({ sourcing }) => {
  if (!sourcing.allowed || sourcing.status !== 'error') return null;
  return (
    <Alert
      severity="warning" sx={{ mb: 1.5 }}
      action={<Button color="inherit" size="small" onClick={sourcing.retry}>重試</Button>}
    >
      {sourcing.error}
    </Alert>
  );
};

/**
 * 批次開啟 Sinsang for a set of SKUs, exactly as the v1 daily page does it.
 *
 * `label` is the aria-label's subject (「批次開啟 Sinsang deefhouse」). `name` is the shop written ON
 * the button, which the 訂貨 screen needs since 2026-09-19: the shop headings are gone, so a
 * per-shop button no longer has a heading above it to say which shop it belongs to.
 */
export const SinsangBatch: React.FC<{ sourcing: Sourcing; label: string; skus: string[]; name?: string }> = ({
  sourcing, label, skus, name,
}) => {
  const [progress, setProgress] = useState<{ key: string; opened: string[]; blocked: boolean }>({
    key: '', opened: [], blocked: false,
  });
  const selection = Array.from(new Set(skus));
  const links = primaryLinks(selection, sourcing.links);
  const key = `${sourcing.identityKey}:${selection.slice().sort().join(',')}`;
  const opened = progress.key === key ? progress.opened.filter((url) => links.indexOf(url) >= 0) : [];
  const remaining = links.filter((url) => opened.indexOf(url) < 0);
  const missing = missingLinkCount(selection, sourcing.links);

  // One failure is one message: the error is reported once, above the groups (SinsangNotice).
  if (!selection.length || !sourcing.allowed || sourcing.status !== 'ready') return null;

  const openBatch = () => {
    const result = openDailySourceBatch(remaining, () => window.open('about:blank', '_blank'));
    setProgress({ key, opened: [...opened, ...result.opened], blocked: result.blocked });
  };

  // Named (per-shop) buttons lead with the shop and keep the verb in the aria-label, so a row of
  // them stays compact; the unnamed one is the whole-order control and says the verb out loud.
  const verb = links.length
    ? (remaining.length ? `${Math.min(10, remaining.length)} 個` : '本批已開啟')
    : (name ? '這家沒有登記連結' : '沒有登記連結');

  return (
    <Box sx={{ mb: 0.9 }}>
      <Box
        component="button" type="button" disabled={!remaining.length} onClick={openBatch}
        aria-label={`批次開啟 Sinsang ${label}`}
        sx={{
          appearance: 'none', cursor: 'pointer', border: '1px solid', borderColor: C.line2,
          borderRadius: '8px', bgcolor: C.card, color: C.fetch, fontWeight: 800, fontSize: 12,
          px: 1, py: '4px', '&:disabled': { opacity: 0.5, cursor: 'default', color: C.ink3 },
        }}
      >
        {name ? `${name} · ${verb}` : `批次開啟 Sinsang · ${verb}`}
      </Box>
      {(opened.length > 0 || missing > 0) && (
        <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3, ml: 0.75 }}>
          {opened.length > 0 ? `已開 ${opened.length}／${links.length}` : ''}
          {missing > 0 ? `${opened.length > 0 ? ' · ' : ''}${missing} 款沒連結` : ''}
        </Typography>
      )}
      {progress.key === key && progress.blocked && (
        <Alert severity="warning" sx={{ mt: 0.75 }}>
          瀏覽器阻擋了分頁；還沒開的連結都留著。允許彈出式視窗後再按一次，或逐筆點商品的下單連結。
        </Alert>
      )}
    </Box>
  );
};
